/**
 * Analytics Service
 * 
 * Handles API calls for chat analytics and metrics
 */

import { api } from './api'
import { logger } from '../utils/logger'

/**
 * Analytics interfaces
 */
export interface ChatMetrics {
  total_conversations: number
  total_messages: number
  active_chatbots: number
  avg_response_time: number
  satisfaction_score: number
}

export interface ConversationTrends {
  date: string
  conversations: number
  messages: number
  avg_session_length: number
}

export interface ChatbotPerformance {
  chatbot_id: string
  chatbot_name: string
  total_conversations: number
  total_messages: number
  avg_session_length: number
  success_rate: number
  last_active?: string
}

export interface HandoffAnalytics {
  total_handoffs: number
  pending_handoffs: number
  completed_handoffs: number
  avg_wait_time_minutes: number
  avg_resolution_time_minutes: number
  success_rate: number
}

export interface TopicsAnalysis {
  topic: string
  frequency: number
  success_rate: number
  avg_satisfaction: number
}

export interface AnalyticsDashboard {
  overview: ChatMetrics
  recent_trends: ConversationTrends[]
  chatbot_performance: ChatbotPerformance[]
  handoff_analytics: HandoffAnalytics
  popular_topics: TopicsAnalysis[]
  period_start: string
  period_end: string
}

export interface AIPerformanceAnalytics {
  total_ai_responses: number
  avg_confidence_score: number
  avg_processing_time_ms: number
  low_confidence_rate: number
  models_used: Array<{
    model: string
    count: number
  }>
}

export interface MessageRatingsAnalytics {
  total_ratings: number
  positive_ratings: number
  negative_ratings: number
  satisfaction_rate: number
  ratings_by_role: Array<{
    role: string
    positive: number
    negative: number
    satisfaction_rate: number
  }>
}

export interface CustomerJourneyAnalytics {
  total_sessions: number
  traffic_sources: Array<{
    source: string
    sessions: number
    percentage: number
  }>
  device_breakdown: Array<{
    device: string
    sessions: number
    percentage: number
  }>
  avg_session_duration_minutes: number
}

// Per-chatbot agent stats composite response
export interface AgentStatsOverview {
  total_conversations: number
  total_messages: number
  total_ai_responses: number
  credits_consumed: number
  avg_response_time_ms: number
  satisfaction_rate: number
  positive_ratings: number
  negative_ratings: number
}

export interface AgentStatsAIPerformance {
  avg_confidence: number
  confidence_distribution: { high: number; medium: number; low: number }
  low_confidence_messages: Array<{
    content: string
    user_query: string
    confidence: number
    timestamp: string
  }>
  avg_sources_used: number
  models_breakdown: Array<{ model: string; count: number; avg_confidence: number; credits: number }>
  daily_confidence_trend: Array<{ date: string; avg_confidence: number; response_count: number }>
  credits_per_conversation: number
}

export interface AgentStatsUsers {
  total_unique_users: number
  returning_users: number
  new_users: number
  peak_hours: Array<{ hour: number; count: number }>
  variable_keys: string[]
}

export interface VariableBreakdownRow {
  value: string
  full_value: string
  sessions: number
  messages: number
  credits: number
  last_seen: string | null
}

export interface VariableBreakdownResponse {
  variable: string
  total: number
  page: number
  page_size: number
  total_pages: number
  rows: VariableBreakdownRow[]
}

export interface AgentStatsHandoffs {
  total_handoffs: number
  handoff_rate: number
  avg_wait_time_minutes: number
  reasons_breakdown: Array<{ reason: string; count: number }>
}

export interface AgentStatsResponse {
  overview: AgentStatsOverview
  ai_performance: AgentStatsAIPerformance
  users: AgentStatsUsers
  handoffs: AgentStatsHandoffs | null
}

/**
 * Analytics Service Class
 */
class AnalyticsService {

  /**
   * Get overall chat metrics overview
   */
  async getChatOverview(days: number = 30): Promise<ChatMetrics> {
    try {
      logger.debug('Fetching chat overview', {
        service: 'AnalyticsService',
        action: 'get_chat_overview',
        days
      })

      const response = await api.get(`/analytics/overview?days=${days}`)
      
      logger.info('Chat overview fetched successfully', {
        service: 'AnalyticsService',
        action: 'chat_overview_fetched',
        metrics: response.data
      })

      return response.data
    } catch (error) {
      logger.error('Failed to fetch chat overview', {
        service: 'AnalyticsService',
        action: 'get_chat_overview_error',
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      throw new Error('Failed to fetch chat overview')
    }
  }

  /**
   * Get conversation trends over time
   */
  async getConversationTrends(days: number = 7): Promise<ConversationTrends[]> {
    try {
      logger.debug('Fetching conversation trends', {
        service: 'AnalyticsService',
        action: 'get_conversation_trends',
        days
      })

      const response = await api.get(`/analytics/trends?days=${days}`)
      
      logger.info('Conversation trends fetched successfully', {
        service: 'AnalyticsService',
        action: 'conversation_trends_fetched',
        count: response.data.length
      })

      return response.data
    } catch (error) {
      logger.error('Failed to fetch conversation trends', {
        service: 'AnalyticsService',
        action: 'get_conversation_trends_error',
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      throw new Error('Failed to fetch conversation trends')
    }
  }

  /**
   * Get individual chatbot performance metrics
   */
  async getChatbotPerformance(days: number = 30): Promise<ChatbotPerformance[]> {
    try {
      logger.debug('Fetching chatbot performance', {
        service: 'AnalyticsService',
        action: 'get_chatbot_performance',
        days
      })

      const response = await api.get(`/analytics/chatbots?days=${days}`)
      
      logger.info('Chatbot performance fetched successfully', {
        service: 'AnalyticsService',
        action: 'chatbot_performance_fetched',
        count: response.data.length
      })

      return response.data
    } catch (error) {
      logger.error('Failed to fetch chatbot performance', {
        service: 'AnalyticsService',
        action: 'get_chatbot_performance_error',
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      throw new Error('Failed to fetch chatbot performance')
    }
  }

  /**
   * Get human handoff analytics
   */
  async getHandoffAnalytics(days: number = 30): Promise<HandoffAnalytics> {
    try {
      logger.debug('Fetching handoff analytics', {
        service: 'AnalyticsService',
        action: 'get_handoff_analytics',
        days
      })

      const response = await api.get(`/analytics/handoffs?days=${days}`)
      
      logger.info('Handoff analytics fetched successfully', {
        service: 'AnalyticsService',
        action: 'handoff_analytics_fetched',
        analytics: response.data
      })

      return response.data
    } catch (error) {
      logger.error('Failed to fetch handoff analytics', {
        service: 'AnalyticsService',
        action: 'get_handoff_analytics_error',
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      throw new Error('Failed to fetch handoff analytics')
    }
  }

  /**
   * Get complete analytics dashboard data
   */
  async getAnalyticsDashboard(days: number = 30, chatbotId?: string): Promise<AnalyticsDashboard> {
    try {
      logger.debug('Fetching complete analytics dashboard', {
        service: 'AnalyticsService',
        action: 'get_analytics_dashboard',
        days
      })

      const url = `/analytics/dashboard?days=${days}${chatbotId ? `&chatbot_id=${chatbotId}` : ''}`
      const response = await api.get(url)
      
      logger.info('Analytics dashboard fetched successfully', {
        service: 'AnalyticsService',
        action: 'analytics_dashboard_fetched',
        period: `${response.data.period_start} to ${response.data.period_end}`
      })

      return response.data
    } catch (error) {
      logger.error('Failed to fetch analytics dashboard', {
        service: 'AnalyticsService',
        action: 'get_analytics_dashboard_error',
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      throw new Error('Failed to fetch analytics dashboard')
    }
  }

  /**
   * Get analytics for specific date range
   */
  async getAnalyticsForDateRange(
    startDate: Date,
    endDate: Date
  ): Promise<AnalyticsDashboard> {
    try {
      const days = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
      
      logger.debug('Fetching analytics for date range', {
        service: 'AnalyticsService',
        action: 'get_analytics_date_range',
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        days
      })

      return await this.getAnalyticsDashboard(days)
    } catch (error) {
      logger.error('Failed to fetch analytics for date range', {
        service: 'AnalyticsService',
        action: 'get_analytics_date_range_error',
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      throw new Error('Failed to fetch analytics for date range')
    }
  }

  /**
   * Get AI performance analytics
   */
  async getAIPerformanceAnalytics(days: number = 30, chatbotId?: string): Promise<AIPerformanceAnalytics> {
    try {
      logger.debug('Fetching AI performance analytics', {
        service: 'AnalyticsService',
        action: 'get_ai_performance',
        days
      })

      const url = `/analytics/ai-performance?days=${days}${chatbotId ? `&chatbot_id=${chatbotId}` : ''}`
      const response = await api.get(url)
      
      logger.info('AI performance analytics fetched successfully', {
        service: 'AnalyticsService',
        action: 'ai_performance_fetched',
        data: response.data
      })

      return response.data
    } catch (error) {
      logger.error('Failed to fetch AI performance analytics', {
        service: 'AnalyticsService',
        action: 'get_ai_performance_error',
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      throw new Error('Failed to fetch AI performance analytics')
    }
  }

  /**
   * Get message ratings analytics (thumbs up/down)
   */
  async getMessageRatingsAnalytics(days: number = 30, chatbotId?: string): Promise<MessageRatingsAnalytics> {
    try {
      logger.debug('Fetching message ratings analytics', {
        service: 'AnalyticsService',
        action: 'get_message_ratings',
        days
      })

      const url = `/analytics/message-ratings?days=${days}${chatbotId ? `&chatbot_id=${chatbotId}` : ''}`
      const response = await api.get(url)
      
      logger.info('Message ratings analytics fetched successfully', {
        service: 'AnalyticsService',
        action: 'message_ratings_fetched',
        data: response.data
      })

      return response.data
    } catch (error) {
      logger.error('Failed to fetch message ratings analytics', {
        service: 'AnalyticsService',
        action: 'get_message_ratings_error',
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      throw new Error('Failed to fetch message ratings analytics')
    }
  }

  /**
   * Get customer journey analytics
   */
  async getCustomerJourneyAnalytics(days: number = 30, chatbotId?: string): Promise<CustomerJourneyAnalytics> {
    try {
      logger.debug('Fetching customer journey analytics', {
        service: 'AnalyticsService',
        action: 'get_customer_journey',
        days
      })

      const url = `/analytics/customer-journey?days=${days}${chatbotId ? `&chatbot_id=${chatbotId}` : ''}`
      const response = await api.get(url)
      
      logger.info('Customer journey analytics fetched successfully', {
        service: 'AnalyticsService',
        action: 'customer_journey_fetched',
        data: response.data
      })

      return response.data
    } catch (error) {
      logger.error('Failed to fetch customer journey analytics', {
        service: 'AnalyticsService',
        action: 'get_customer_journey_error',
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      throw new Error('Failed to fetch customer journey analytics')
    }
  }

  /**
   * Get per-chatbot agent stats (composite endpoint)
   */
  async getAgentStats(chatbotId: string, days: number = 30): Promise<AgentStatsResponse> {
    try {
      logger.debug('Fetching agent stats', {
        service: 'AnalyticsService',
        action: 'get_agent_stats',
        chatbotId,
        days
      })

      const response = await api.get(`/analytics/agent-stats?chatbot_id=${chatbotId}&days=${days}`)

      logger.info('Agent stats fetched successfully', {
        service: 'AnalyticsService',
        action: 'agent_stats_fetched',
        chatbotId
      })

      return response.data
    } catch (error) {
      logger.error('Failed to fetch agent stats', {
        service: 'AnalyticsService',
        action: 'get_agent_stats_error',
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      throw new Error('Failed to fetch agent stats')
    }
  }

  /**
   * Get breakdown for a single user variable (loaded on demand)
   */
  async getVariableBreakdown(
    chatbotId: string,
    variable: string,
    days: number = 30,
    page: number = 1,
    pageSize: number = 10,
    search?: string,
  ): Promise<VariableBreakdownResponse> {
    try {
      let url = `/analytics/agent-variable?chatbot_id=${chatbotId}&variable=${encodeURIComponent(variable)}&days=${days}&page=${page}&page_size=${pageSize}`
      if (search) url += `&search=${encodeURIComponent(search)}`
      const response = await api.get(url)
      return response.data
    } catch (error) {
      logger.error('Failed to fetch variable breakdown', {
        service: 'AnalyticsService',
        action: 'get_variable_breakdown_error',
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      throw new Error('Failed to fetch variable breakdown')
    }
  }

  /**
   * Get real-time analytics summary (lightweight)
   */
  async getAnalyticsSummary(): Promise<{
    conversations_today: number
    messages_today: number
    active_agents: number
    pending_handoffs: number
  }> {
    try {
      logger.debug('Fetching analytics summary', {
        service: 'AnalyticsService',
        action: 'get_analytics_summary'
      })

      // Use 1-day overview for real-time summary
      const overview = await this.getChatOverview(1)
      const handoffs = await this.getHandoffAnalytics(1)
      
      // Get active agents count from live chat API
      const liveChatResponse = await api.get('/live-chat/status/summary')
      
      const summary = {
        conversations_today: overview.total_conversations,
        messages_today: overview.total_messages,
        active_agents: liveChatResponse.data.agents_online || 0,
        pending_handoffs: handoffs.pending_handoffs
      }
      
      logger.info('Analytics summary fetched successfully', {
        service: 'AnalyticsService',
        action: 'analytics_summary_fetched',
        summary
      })

      return summary
    } catch (error) {
      logger.error('Failed to fetch analytics summary', {
        service: 'AnalyticsService',
        action: 'get_analytics_summary_error',
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      throw new Error('Failed to fetch analytics summary')
    }
  }
}

// Create and export service instance
export const analyticsService = new AnalyticsService()

// Export service class for testing
export { AnalyticsService }